const CACHE_KEY = 'judge0_languages';
const JUDGE0_URL = 'https://ce.judge0.com/languages/';

// Map from base language name to Monaco editor language ID
const MONACO_MAP = {
  'Assembly': 'plaintext',
  'Bash': 'shell',
  'Basic': 'vb',
  'C': 'c',
  'C#': 'csharp',
  'C++': 'cpp',
  'Common Lisp': 'plaintext',
  'D': 'd',
  'Elixir': 'elixir',
  'Erlang': 'erlang',
  'Fortran': 'fortran',
  'Go': 'go',
  'Haskell': 'haskell',
  'Java': 'java',
  'JavaScript': 'javascript',
  'Kotlin': 'kotlin',
  'Lua': 'lua',
  'OCaml': 'caml',
  'Octave': 'matlab',
  'Pascal': 'pascal',
  'PHP': 'php',
  'Prolog': 'plaintext',
  'Python': 'python',
  'Ruby': 'ruby',
  'Rust': 'rust',
  'Swift': 'swift',
  'TypeScript': 'typescript',
};

// "Java (OpenJDK 13.0.1)" -> "Java"
export function extractBaseName(fullName) {
  return fullName.replace(/\s*\(.*\)$/, '').trim();
}

// Get Monaco editor language ID for a base language name
export function getMonacoId(baseName) {
  return MONACO_MAP[baseName] || baseName.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Fetch all active languages from Judge0 API.
 * Results are cached in sessionStorage to avoid repeated network calls.
 * @returns {Promise<Array<{id: number, name: string}>>}
 */
export async function fetchJudgeLanguages() {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) return JSON.parse(cached);

    const res = await fetch(JUDGE0_URL);
    if (!res.ok) throw new Error('Failed to fetch Judge0 languages');
    const data = await res.json();

    const filtered = data.filter(l => l.name !== 'Plain Text' && l.name !== 'Executable');
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(filtered));
    return filtered;
  } catch {
    return [];
  }
}

/**
 * Convert raw Judge0 languages to a deduplicated list of {value, label, id} objects.
 * Used by QuestionForm's TagSelector for the "Categories" field.
 * @param {Array<{id: number, name: string}>} rawLanguages
 * @returns {Array<{value: string, label: string, id: number}>}
 */
export function toCategoryOptions(rawLanguages) {
  const seen = new Set();
  const result = [];
  for (const l of rawLanguages) {
    const base = extractBaseName(l.name);
    if (!seen.has(base)) {
      seen.add(base);
      result.push({ value: base, label: base, id: l.id });
    }
  }
  return result.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Convert raw Judge0 languages to the LANGUAGES array format used by sandbox components.
 * Deduplicates by base name (keeps first occurrence per language name).
 * @param {Array<{id: number, name: string}>} rawLanguages
 * @returns {Array<{id: number, value: string, label: string, monacoId: string, codeKey: string}>}
 */
export function toSandboxLanguages(rawLanguages) {
  const seen = new Set();
  const result = [];
  for (const l of rawLanguages) {
    const label = extractBaseName(l.name);
    if (seen.has(label)) continue;
    seen.add(label);
    const monacoId = getMonacoId(label);
    result.push({
      id: l.id,
      value: monacoId,   // used as Monaco language ID and local state key
      label,             // display name
      monacoId,
      codeKey: label,    // matches sampleCode keys stored in DB ("Java", "JavaScript", etc.)
    });
  }
  return result;
}

/**
 * Convert sampleCode from DB format {"JavaScript": "code"} to Monaco key format {"javascript": "code"}.
 * Handles special mappings like "C++" -> "cpp", "C#" -> "csharp".
 * @param {Object} sampleCode
 * @returns {Object}
 */
export function normalizeSampleCode(sampleCode) {
  if (!sampleCode) return {};
  const result = {};
  for (const [key, value] of Object.entries(sampleCode)) {
    const newKey = getMonacoId(key);
    result[newKey] = value;
  }
  return result;
}
